import { useEffect, useState } from "react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/auth/AuthContext";
import { LabsStep } from "@/components/supplement/LabsStep";
import EightWeekPlan from "@/components/supplement/EightWeekPlan";
import { PlanView } from "@/components/supplement/PlanView";
import { ProfileStep } from "@/components/supplement/ProfileStep";
import { QuestionStep } from "@/components/supplement/QuestionStep";
import { EMPTY_PROFILE } from "@/components/supplement/supplementConstants";
import {
  deleteSupplementChecks,
  getLatestSupplementCheck,
  getSupplementScreening,
  saveSupplementCheck,
} from "@/lib/supplementApi";
import "@/pages/SupplementCheck.css";

const initialAnswer = (question) => {
  if (question.type === "grid") return Object.fromEntries(question.rows.map((row) => [row.id, row.default ?? 1]));
  if (question.type === "list" || (question.type === "multi" && question.id === "q_goals")) return [];
  return undefined;
};

export default function SupplementCheck() {
  const { user, loading: authLoading } = useAuth();
  const [screening, setScreening] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("start");
  const [profile, setProfile] = useState({ ...EMPTY_PROFILE });
  const [answers, setAnswers] = useState({});
  const [labs, setLabs] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [plan, setPlan] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loginConsent, setLoginConsent] = useState(false);
  const [loginError, setLoginError] = useState(false);

  useEffect(() => {
    let active = true;
    getSupplementScreening()
      .then((data) => { if (active) setScreening(data); })
      .catch(() => { if (active) setMode("error"); })
      .finally(() => { if (active && !user) setLoading(false); });
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    getLatestSupplementCheck()
      .then((data) => {
        if (!active) return;
        if (!data.empty) {
          setPlan(data.result);
          setSavedAt(data.created_at);
          setMode("result");
        }
      })
      .catch(() => { if (active) setMode("error"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [user]);

  const question = screening?.questions?.[questionIndex];
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [mode, questionIndex]);

  useEffect(() => {
    if (mode !== "question" || !question || answers[question.id] !== undefined) return;
    const value = initialAnswer(question);
    if (value !== undefined) setAnswers((current) => ({ ...current, [question.id]: value }));
  }, [answers, mode, question]);

  const reset = () => {
    setProfile({ ...EMPTY_PROFILE });
    setAnswers({});
    setLabs({});
    setQuestionIndex(0);
    setPlan(null);
    setSavedAt(null);
    setMode("profile");
  };

  const nextQuestion = () => {
    if (questionIndex === screening.questions.length - 1) {
      setMode("labs");
      return;
    }
    setQuestionIndex((index) => index + 1);
  };

  const previousQuestion = () => {
    if (questionIndex === 0) {
      setMode("profile");
      return;
    }
    setQuestionIndex((index) => index - 1);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const response = await saveSupplementCheck(profile, answers, labs);
      setPlan(response.result);
      setSavedAt(null);
      setMode("result");
    } catch {
      setMode("error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete your saved answers and plan?")) return;
    await deleteSupplementChecks();
    setPlan(null);
    setSavedAt(null);
    setMode("start");
  };

  const changeLab = (key, value) => {
    setLabs((current) => {
      if (!value) {
        const next = { ...current };
        delete next[key];
        return next;
      }
      return { ...current, [key]: value };
    });
  };

  const pageLoading = authLoading || loading || !screening;

  const beginGoogleSignIn = () => {
    if (!loginConsent) {
      setLoginError(true);
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/auth/callback?returnTo=${encodeURIComponent("/supplement-check")}&consent=1`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="supplement-page" data-testid="supplement-check-page">
      <Header />
      <main className="supplement-main">
        <header className="supplement-intro">
          <h1 data-testid="supplement-page-title">Supplement Check</h1>
          <p data-testid="supplement-page-intro">Answer a few questions. You get a short plan for the next 8 weeks.</p>
        </header>

        {pageLoading ? (
          <div className="supplement-loading" data-testid="supplement-loading-state" />
        ) : !user ? (
          <section className="supplement-tool supplement-tool--signin" data-testid="supplement-logged-out-view">
            <label className="supplement-consent" data-testid="supplement-signin-consent-label">
              <Checkbox checked={loginConsent} onCheckedChange={(value) => { setLoginConsent(value === true); setLoginError(false); }} data-testid="supplement-signin-consent-checkbox" />
              <span>I agree that Supple may store my answers and profile to create my reports and show my history. I can ask hello@supple.care to delete my data at any time.</span>
            </label>
            {loginError && <p className="supplement-error" role="alert" data-testid="supplement-signin-consent-error">Please tick the box so we can save your answers.</p>}
            <Button onClick={beginGoogleSignIn} className={`supplement-signin-action ${loginConsent ? "is-ready" : "is-inactive"}`} data-testid="supplement-signin-button">Sign me in</Button>
          </section>
        ) : mode === "start" ? (
          <section className="supplement-tool supplement-tool--start" data-testid="supplement-start-view">
            <Button onClick={() => setMode("profile")} className="supplement-primary-action" data-testid="supplement-start-button">Start</Button>
          </section>
        ) : mode === "profile" ? (
          <ProfileStep screening={screening} profile={profile} onChange={(key, value) => setProfile((current) => ({ ...current, [key]: value }))} onNext={() => { setProfile((current) => ({ ...current, age: Number(current.age), weight_kg: Number(current.weight_kg) })); setMode("question"); }} />
        ) : mode === "question" ? (
          <QuestionStep key={question.id} question={question} questionNumber={questionIndex + 1} questionCount={screening.questions.length} value={answers[question.id]} onChange={(value) => setAnswers((current) => ({ ...current, [question.id]: value }))} onBack={previousQuestion} onNext={nextQuestion} />
        ) : mode === "labs" ? (
          <LabsStep labsOptional={screening.labs_optional} labs={labs} onChange={changeLab} onBack={() => { setQuestionIndex(screening.questions.length - 1); setMode("question"); }} onSubmit={submit} saving={saving} />
        ) : mode === "result" && plan ? (
          <><PlanView result={plan} savedAt={savedAt} onRetake={reset} onDelete={remove} /><EightWeekPlan /></>
        ) : (
          <section className="supplement-tool" data-testid="supplement-error-view">
            <p className="supplement-error">Something went wrong. Please try again.</p>
            <Button variant="ghost" onClick={() => setMode("labs")} data-testid="supplement-error-back-button">Back</Button>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}